import styles from './DynamicFormBuilder.module.css';

function pathsEqual(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}

function hasErrorForPath(errors, keyPath) {
  if (!Array.isArray(errors) || errors.length === 0 || !keyPath) return false;
  const prefix = `${keyPath}:`;
  return errors.some((msg) => typeof msg === 'string' && msg.startsWith(prefix));
}

export default function FieldTree({
  fields,
  parentPath = [],
  parentKeyPath = '',
  selectedPath,
  errors = [],
  onSelect,
  onAddAfter,
  onAddChild,
  onRemove,
  onMoveUp,
  onMoveDown,
}) {
  if (!Array.isArray(fields) || fields.length === 0) {
    return <p className="m-0 small text-muted fst-italic">No fields yet.</p>;
  }

  const listClass = parentPath.length === 0
    ? 'list-unstyled m-0 p-0'
    : `list-unstyled m-0 p-0 ${styles.treeNested}`;

  return (
    <ul className={listClass}>
      {fields.map((field, i) => {
        const nodePath = [...parentPath, i];
        const keyPath = parentKeyPath
          ? (field?.key ? `${parentKeyPath}.${field.key}` : parentKeyPath)
          : (field?.key || '');
        const isGroup = field?.type === 'group';
        const children = isGroup && Array.isArray(field.children) ? field.children : [];
        const isSelected = pathsEqual(nodePath, selectedPath);
        const isFirst = i === 0;
        const isLast = i === fields.length - 1;
        const hasError = hasErrorForPath(errors, keyPath);
        const stop = (e) => e.stopPropagation();

        const buttonClasses = [styles.nodeButton];
        if (isSelected) buttonClasses.push(styles.nodeSelected);
        if (hasError) buttonClasses.push(styles.nodeError);

        return (
          <li key={nodePath.join('.')}>
            <div className={`d-flex align-items-center gap-1 ${styles.nodeRow}`}>
              <button
                type="button"
                className={buttonClasses.join(' ')}
                onClick={() => onSelect(nodePath)}
              >
                <span className="fw-semibold small">{field?.key || '(unkeyed)'}</span>
                <span className="small text-muted text-lowercase">{field?.type || '?'}</span>
              </button>

              <div className={`d-flex gap-1 ${styles.nodeActions}`} onClick={stop}>
                <button
                  type="button"
                  className={`btn btn-sm btn-outline-secondary ${styles.iconButton}`}
                  title="Move up"
                  disabled={isFirst}
                  onClick={() => onMoveUp(nodePath)}
                >↑</button>
                <button
                  type="button"
                  className={`btn btn-sm btn-outline-secondary ${styles.iconButton}`}
                  title="Move down"
                  disabled={isLast}
                  onClick={() => onMoveDown(nodePath)}
                >↓</button>
                <button
                  type="button"
                  className={`btn btn-sm btn-outline-secondary ${styles.iconButton}`}
                  title="Add field below"
                  onClick={() => onAddAfter(nodePath)}
                >+</button>
                {isGroup && (
                  <button
                    type="button"
                    className={`btn btn-sm btn-outline-secondary ${styles.iconButton}`}
                    title="Add child field"
                    onClick={() => onAddChild(nodePath)}
                  >+↳</button>
                )}
                <button
                  type="button"
                  className={`btn btn-sm btn-outline-danger ${styles.iconButton}`}
                  title="Delete field"
                  onClick={() => onRemove(nodePath)}
                >×</button>
              </div>
            </div>

            {children.length > 0 && (
              <FieldTree
                fields={children}
                parentPath={nodePath}
                parentKeyPath={keyPath}
                selectedPath={selectedPath}
                errors={errors}
                onSelect={onSelect}
                onAddAfter={onAddAfter}
                onAddChild={onAddChild}
                onRemove={onRemove}
                onMoveUp={onMoveUp}
                onMoveDown={onMoveDown}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}
